import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { Chip, Box } from '@mui/material';
import { Alert, Row, Col, Button } from 'reactstrap';
import Breadcrumbs from '../../shims/commonUiBreadcrumbs';
import { dataGridTheme, dataGridSx } from '../../shims/muiDataGridTheme';
import { statusColorMap } from '../../constants/statusColors';
import { useDebounce } from '../../hooks/useDebounce';
import EmptyState from '../../components/tickets/EmptyState';
import SearchBar from '../../components/tickets/SearchBar';
import StatusFilter from '../../components/tickets/StatusFilter';
import DateRangePicker from '../../components/tickets/DateRangePicker';
import FilterChips from '../../components/tickets/FilterChips';
import { listTickets } from '../../api/ticketApi';

const PAGE_SIZE = 20;
const POLL_MS = 30000;
const SEARCH_DEBOUNCE_MS = 500;

const listGridSx = {
  ...dataGridSx,
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    justifyContent: 'flex-start !important',
  },
  '& .MuiDataGrid-columnHeader': {
    justifyContent: 'flex-start',
  },
  '& .ticket-id-cell': {
    whiteSpace: 'nowrap !important',
    overflow: 'visible !important',
  },
  '& .MuiDataGrid-row': {
    cursor: 'default',
  },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium' }).format(d);
}

export default function TicketListPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const dateRangeInvalid = Boolean(dateFrom && dateTo && dateTo < dateFrom);
  const effectiveDateFrom = dateRangeInvalid ? undefined : dateFrom || undefined;
  const effectiveDateTo = dateRangeInvalid ? undefined : dateTo || undefined;

  const filters = { search, statuses, dateFrom, dateTo };
  const hasActiveFilters = Boolean(
    debouncedSearch || statuses.length > 0 || dateFrom || dateTo
  );

  const clearAllFilters = () => {
    setSearch('');
    setStatuses([]);
    setDateFrom('');
    setDateTo('');
  };

  const fetchTickets = useCallback(async () => {
    try {
      setError(null);
      const res = await listTickets({
        page: page + 1,
        pageSize,
        search: debouncedSearch || undefined,
        status: statuses.length ? statuses : undefined,
        dateFrom: effectiveDateFrom,
        dateTo: effectiveDateTo,
      });
      setTickets(res.tickets || []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(err?.json?.message || err?.json?.error || 'Failed to load tickets.');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearch, statuses, effectiveDateFrom, effectiveDateTo]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statuses, dateFrom, dateTo]);

  useEffect(() => {
    setIsLoading(true);
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    if (document.visibilityState === 'hidden') return;
    const id = setInterval(fetchTickets, POLL_MS);
    return () => clearInterval(id);
  }, [fetchTickets]);

  const navigateToTicket = useCallback(
    (id) => navigate(`/ticketing/tickets/${id}`),
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: 'ticketId',
        headerName: 'Ticket ID',
        minWidth: 130,
        flex: 0,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'ticket-id-cell',
        renderCell: (params) => (
          <Box
            component="span"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              navigateToTicket(params.row.ticketId);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                navigateToTicket(params.row.ticketId);
              }
            }}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              whiteSpace: 'nowrap',
              fontWeight: 500,
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 200,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 168,
        flex: 0,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Chip label={params.value} color={statusColorMap[params.value] || 'default'} size="small" />
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 120,
        flex: 0,
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: 'updatedAt',
        headerName: 'Last Updated',
        width: 120,
        flex: 0,
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value) => formatDate(value),
      },
    ],
    [navigateToTicket]
  );

  const rows = tickets.map((t) => ({
    id: t.ticketId,
    ticketId: t.ticketId,
    subject: t.subject,
    status: t.status,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
  }));

  return (
    <Fragment>
      <div className="d-flex mtop-1 flex-wrap align-items-center justify-content-between gap-2">
        <Breadcrumbs mainTitle="Tickets" />
        <Button color="primary" onClick={() => navigate('/ticketing/create')}>
          Create ticket
        </Button>
      </div>

      <Row className="g-2 mb-2 align-items-center">
        <Col xs={12} md="auto">
          <SearchBar value={search} onChange={setSearch} />
        </Col>
        <Col xs={12} md="auto">
          <StatusFilter value={statuses} onChange={setStatuses} />
        </Col>
        <Col xs={12} md="auto">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChangeFrom={setDateFrom}
            onChangeTo={setDateTo}
          />
        </Col>
        {hasActiveFilters && (
          <Col xs={12} md="auto">
            <Button color="secondary" outline size="sm" onClick={clearAllFilters}>
              Clear filters
            </Button>
          </Col>
        )}
      </Row>

      {dateRangeInvalid && (
        <Alert color="warning" className="mb-2 py-2">
          End date must be on or after start date. Date filters are ignored until corrected.
        </Alert>
      )}

      <FilterChips
        filters={filters}
        onClearSearch={() => setSearch('')}
        onClearStatus={(s) => setStatuses((prev) => prev.filter((x) => x !== s))}
        onClearDateFrom={() => setDateFrom('')}
        onClearDateTo={() => setDateTo('')}
        onClearAll={clearAllFilters}
      />

      {error && (
        <Alert color="danger" toggle={() => setError(null)} className="mb-2">
          {error}
        </Alert>
      )}

      <div className="card">
        <div className="card-body p-0">
          <div className="groups-table">
            <ThemeProvider theme={dataGridTheme}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={isLoading}
                paginationMode="server"
                rowCount={total}
                pageSizeOptions={[10, 20, 50]}
                paginationModel={{ page, pageSize }}
                onPaginationModelChange={({ page: p, pageSize: ps }) => {
                  setPage(p);
                  setPageSize(ps);
                }}
                slots={{
                  noRowsOverlay: () =>
                    !isLoading && rows.length === 0 ? (
                      <EmptyState hasActiveFilters={hasActiveFilters} />
                    ) : null,
                }}
                sx={{ ...listGridSx, border: 'none', minHeight: 400 }}
                autoHeight
                disableColumnMenu
              />
            </ThemeProvider>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
